<app-navbar></app-navbar>

<div class="min-h-screen bg-section pt-24 pb-20 px-4 sm:px-6 lg:px-8 font-serif text-body-text selection:bg-btn/30 relative overflow-hidden">

  <!-- Botanical Decorations -->
  <div class="absolute -top-12 -left-12 w-64 h-64 opacity-30 pointer-events-none z-0">
    <img src="amla-top-left.jpg" alt="" class="w-full h-full object-contain mix-blend-multiply">
  </div>
  <div class="absolute -bottom-12 -right-12 w-64 h-64 opacity-20 pointer-events-none z-0">
    <img src="amla-bottom-right.png" alt="" class="w-full h-full object-contain mix-blend-multiply rotate-12">
  </div>

  <div class="max-w-4xl mx-auto relative z-10 space-y-20">

    <!-- Hero Section -->
    <div class="text-center space-y-6 animate-fade-in">
      <p class="text-xs uppercase tracking-[0.3em] text-btn opacity-70">Our Purpose</p>
      <h1 class="text-4xl sm:text-6xl font-serif text-heading tracking-tight leading-tight">Restoring Trust in<br>Natural Wellness.</h1>
      <div class="w-24 h-px bg-btn mx-auto opacity-40"></div>
      <p class="text-lg sm:text-xl text-[#5C504B] leading-relaxed max-w-2xl mx-auto font-light">
        AARVYA was created to bring trust, purity, and authenticity back to natural wellness — because when it comes to health, trust should never be built on marketing. It should be built on quality.
      </p>
    </div>

    <!-- Why AARVYA Was Created -->
    <div class="bg-white border border-[#E5DDD5] rounded-sm p-8 sm:p-12 shadow-sm animate-fade-in-up relative overflow-hidden">
      <div class="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-btn to-transparent opacity-30"></div>
      <div class="space-y-5">
        <p class="text-xs uppercase tracking-[0.25em] text-btn">Why AARVYA Was Created</p>
        <h2 class="text-2xl sm:text-3xl font-serif text-heading">A Simple But Meaningful Purpose</h2>
        <p class="text-[#5C504B] leading-relaxed font-light">
          In today's health market, many products are shaped by marketing trends and exaggerated claims, making it difficult for consumers to know what they can truly rely on. We believed natural wellness should be transparent, consistent, and built on genuine ingredient integrity.
        </p>
        <p class="text-[#5C504B] leading-relaxed font-light">
          Rather than focusing on shortcuts or miracle promises, AARVYA was built on a different approach — one that prioritizes pure ingredients, responsible sourcing, and internationally recognized quality standards.
        </p>
        <p class="text-[#5C504B] leading-relaxed font-light">
          The products associated with AARVYA follow rigorous certification and safety frameworks including USDA Organic, BioAgriCert Organic, GMP manufacturing standards, ISO-certified processes, and FSSAI compliance — some of the most demanding quality systems followed globally.
        </p>
      </div>
    </div>

    <!-- About AARVYA -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-12 items-start animate-fade-in-up">
      <div class="space-y-5">
        <p class="text-xs uppercase tracking-[0.25em] text-btn">About AARVYA</p>
        <h2 class="text-2xl sm:text-3xl font-serif text-heading">Rooted in Tradition.<br>Guided by Science.</h2>
        <p class="text-[#5C504B] leading-relaxed font-light">
          At AARVYA, we believe that nature provides some of the most powerful solutions for everyday wellness. Inspired by traditional Ayurvedic wisdom and supported by modern quality standards, AARVYA focuses on delivering pure herbal and plant-based nutrition designed for daily wellbeing.
        </p>
        <p class="text-[#5C504B] leading-relaxed font-light">
          Our ingredients are sourced from trusted farms and processed under controlled quality standards to preserve their natural nutrients, purity, and effectiveness. From traditional herbs to powerful superfoods, AARVYA aims to bring the goodness of nature into modern lifestyles in its most authentic form.
        </p>
      </div>
      <div class="space-y-5">
        <p class="text-xs uppercase tracking-[0.25em] text-btn">Our Philosophy</p>
        <h2 class="text-2xl sm:text-3xl font-serif text-heading">Honest. Consistent.<br>Natural.</h2>
        <p class="text-[#5C504B] leading-relaxed font-light">
          We do not believe in exaggerated promises or miracle ingredients. True wellness comes from simple, natural ingredients that support the body when used regularly. That is why our products focus on clean, powerful herbal ingredients inspired by Ayurvedic tradition, without unnecessary additives or misleading claims.
        </p>
        <div class="border-l-2 border-btn pl-4 mt-4">
          <p class="text-heading font-serif italic text-sm leading-relaxed">
            "No Magic Claims. Just Pure Ingredients. Consistent Quality."
          </p>
        </div>
      </div>
    </div>

    <!-- Quality & Certifications -->
    <div class="bg-white border border-[#E5DDD5] rounded-sm p-8 sm:p-12 shadow-sm animate-fade-in-up relative overflow-hidden">
      <div class="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-btn to-transparent opacity-30"></div>
      <div class="text-center space-y-8">
        <div>
          <p class="text-xs uppercase tracking-[0.25em] text-btn">Quality & Certifications</p>
          <h2 class="text-2xl sm:text-3xl font-serif text-heading mt-2">Standards That Matter</h2>
          <p class="text-[#5C504B] font-light mt-3 max-w-xl mx-auto leading-relaxed">These standards represent some of the most rigorous organic and safety systems in the world, ensuring every product meets strict requirements.</p>
        </div>
        <div class="grid grid-cols-2 sm:grid-cols-5 gap-6">
          <div *ngFor="let cert of certifications" class="flex flex-col items-center gap-2 p-4 bg-section rounded-sm border border-[#E5DDD5]">
            <span class="text-2xl">{{ cert.icon }}</span>
            <p class="text-[10px] uppercase tracking-widest font-bold text-heading text-center leading-tight">{{ cert.name }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Quality Process -->
    <div class="space-y-8 animate-fade-in-up">
      <div class="text-center">
        <p class="text-xs uppercase tracking-[0.25em] text-btn">The AARVYA Quality Process</p>
        <h2 class="text-2xl sm:text-3xl font-serif text-heading mt-2">From Farm to You</h2>
      </div>
      <div class="grid grid-cols-1 sm:grid-cols-5 gap-4">
        <div *ngFor="let step of qualitySteps; let i = index" class="relative flex flex-col items-center text-center space-y-3 p-5 bg-white border border-[#E5DDD5] rounded-sm shadow-sm">
          <div class="w-10 h-10 rounded-full bg-btn/10 flex items-center justify-center text-btn font-serif font-bold text-lg">{{ i + 1 }}</div>
          <h3 class="text-xs uppercase tracking-widest font-bold text-heading">{{ step.title }}</h3>
          <p class="text-[#5C504B] text-xs leading-relaxed font-light">{{ step.description }}</p>
        </div>
      </div>
    </div>

    <!-- Commitment Banner -->
    <div class="text-center space-y-6 py-16 border-t border-b border-[#E5DDD5] animate-fade-in">
      <p class="text-xs uppercase tracking-[0.3em] text-btn">Our Commitment</p>
      <blockquote class="text-2xl sm:text-3xl font-serif text-heading leading-relaxed max-w-2xl mx-auto italic">
        "Every AARVYA product reflects our commitment to deliver pure, trustworthy, and premium natural wellness."
      </blockquote>
      <p class="text-[#5C504B] font-light max-w-xl mx-auto leading-relaxed">
        We are dedicated to maintaining the highest standards of quality, transparency, and authenticity, helping people make better choices for their everyday wellbeing.
      </p>
      <div class="w-24 h-px bg-btn mx-auto opacity-40"></div>
      <p class="text-heading font-serif tracking-wider text-sm">AARVYA — Pure by Nature. Trusted by You.</p>
    </div>

    <!-- CTA -->
    <div class="text-center space-y-6 pb-8 animate-fade-in">
      <a routerLink="/home" class="inline-block px-12 py-4 bg-heading text-white font-serif uppercase text-[10px] tracking-[0.3em] font-bold hover:bg-[#2a1f1a] transition-all rounded-sm shadow-xl">
        Explore Our Products
      </a>
    </div>

  </div>
</div>
